import { DialogEvent } from "./enum";

export class CharacterDTO {
    character_id: string;
    name: string;
    sprite_data: SpriteDataDTO;
    frame_index: number;
    type: string;

    // Multi-tenant fields
    project_id: string | null;
    publication_id: string | null;
    save_id: string | null;
    is_template: boolean;
    account_id: string | null;
}

export class PointDTO {
    x: number;
    y: number;
}

export class NPCDTO extends CharacterDTO {
    dialog_event: DialogEvent;
    position?: PointDTO;
}

export class StatDTO {
    constructor(statDto?: StatDTO) {
        if (!statDto) {
            return;
        }
        this.stat_id = statDto.stat_id;
        this.value = statDto.value;
    }

    stat_id: string;
    value: number;
}

export class StatContainerDTO {
    stats: Array<StatDTO>;
}

export class CombatCharacterDTO extends CharacterDTO {
    combat_id: number;
    /*    max_hp: number;
    hp: number;
    base_damage: number;
    defense: number;*/
    equipments: Array<EquipmentDTO>;
    alive: boolean;
    stat_container: StatContainerDTO;

    constructor(combatCharacter?: CombatCharacterDTO) {
        super();
        if (!combatCharacter) {
            return;
        }
        this.alive = combatCharacter.alive;
        this.stat_container = combatCharacter.stat_container;
        this.equipments = combatCharacter.equipments;
        this.combat_id = combatCharacter.combat_id;
        this.character_id = combatCharacter.character_id;
        this.name = combatCharacter.name;
        this.sprite_data = combatCharacter.sprite_data;
        this.frame_index = combatCharacter.frame_index;
        this.type = combatCharacter.type;
        // Copy multi-tenant fields
        this.project_id = combatCharacter.project_id;
        this.publication_id = combatCharacter.publication_id;
        this.save_id = combatCharacter.save_id;
        this.is_template = combatCharacter.is_template;
        this.account_id = combatCharacter.account_id;
    }

    public findStatById(stat_id: string): StatDTO {
        return this.stat_container?.stats.find((stat_dto) => stat_dto.stat_id == stat_id);
    }
}

export class PlayerCharacterDTO extends CombatCharacterDTO {
    constructor(playerCharacter?: PlayerCharacterDTO) {
        super(playerCharacter);
        if (!playerCharacter) {
            return;
        }
        this.xp = playerCharacter.xp;
        this.level = playerCharacter.level;
        this.character_class = playerCharacter.character_class;
        // Explicitly ensure type is set correctly for player characters
        this.type = playerCharacter.type || "PC";
    }

    public static fromCharacter(playerCharacter: PlayerCharacterDTO): PlayerCharacterDTO {
        const instance = new PlayerCharacterDTO(playerCharacter);
        // Ensure type is preserved
        instance.type = playerCharacter.type || "PC";
        return instance;
    }

    xp: number;
    level?: number;
    character_class?: CharacterClassDTO;

    /*    public findStatById(stat_id: string): StatDTO {
        return this.stat_container.stats.find(stat_dto => stat_dto.stat_id == stat_id)
    }*/
}

export class EnemyDTO extends CombatCharacterDTO {
    constructor(enemy?: EnemyDTO) {
        super(enemy);
        if (!enemy) {
            return;
        }
        // Explicitly ensure type is set correctly for enemies
        this.type = enemy.type || "enemy";
    }

    public static fromCharacter(enemy: EnemyDTO): EnemyDTO {
        const instance = new EnemyDTO(enemy);
        // Ensure type is preserved
        instance.type = enemy.type || "enemy";
        return instance;
    }
}

export class AnimationDataDTO {
    up: number[];
    down: number[];
    left: number[];
    right: number[];
}

export class SpriteDataDTO {
    image_path: string;
    // type = atlas or spritesheet
    type: string;
    frame_name: string;
    battle_frame_name: number;
    width: number = 32;
    height: number = 64;
    online_image?: string; // Auto-populated by backend
    animations?: AnimationDataDTO; // 4-frame animation sequences for directional movement
}

export class AnimationDTO {
    animation_key: string;
    spritesheet_key: string;
    frame_keys: Array<number>;
}

export class BattleActionDTO {}

export class AttackResultDTO {
    damage: number;
}

export class AttackDTO extends BattleActionDTO {
    source: CombatCharacterDTO;
    targets: Array<CombatCharacterDTO>;
    results: Array<AttackResultDTO>;
}

export class TurnDTO {
    unit: CombatCharacterDTO;
    action: BattleActionDTO;
}

export class TurnLogDTO {
    turns: Array<TurnDTO>;
}

export class BattleContextDTO {
    account_id: number;
    status: string;
    active_unit: number;
    units: Array<CombatCharacterDTO>;
    rewards: Array<BattleRewardDTO>;
    turn_log: TurnLogDTO;

    constructor(
        account_id: number,
        status: string,
        active_unit: number,
        units: Array<CombatCharacterDTO>,
        rewards: Array<BattleRewardDTO>,
        turn_log: TurnLogDTO
    ) {
        this.account_id = account_id;
        this.status = status;
        this.active_unit = active_unit;
        this.units = units;
        this.rewards = rewards;
        this.turn_log = turn_log;
    }

    public static fromJson(data: BattleContextDTO): BattleContextDTO {
        const units = data.units.map((unit) =>
            unit.type == "PC"
                ? PlayerCharacterDTO.fromCharacter(unit as PlayerCharacterDTO)
                : EnemyDTO.fromCharacter(unit as EnemyDTO)
        );

        return new BattleContextDTO(
            data.account_id,
            data.status,
            data.active_unit,
            units,
            null,
            data.turn_log
        );
    }

    public findLastAttack(): AttackDTO {
        const turnLength = this.turn_log.turns.length;
        return this.turn_log.turns[turnLength - 1].action as AttackDTO;
    }
    //    enemies: Array<EnemyDTO>;
    //    players: Array<PlayerCharacterDTO>;
}

export class BattleContextV2DTO {
    battle_id: string;
    state: string;
    player_units: Array<CombatCharacterDTO>;
    enemy_units: Array<CombatCharacterDTO>;
    turn_number: number;
    turn_order: Array<string>;
    active_unit_id: string | null;
    turn_log: Array<any>;
    battle_log: Array<string>;
    rewards: any;

    constructor(
        battle_id: string,
        state: string,
        player_units: Array<CombatCharacterDTO>,
        enemy_units: Array<CombatCharacterDTO>,
        turn_number: number,
        turn_order: Array<string>,
        active_unit_id: string | null,
        turn_log: Array<any>,
        battle_log: Array<string>,
        rewards: any
    ) {
        this.battle_id = battle_id;
        this.state = state;
        this.player_units = player_units;
        this.enemy_units = enemy_units;
        this.turn_number = turn_number;
        this.turn_order = turn_order;
        this.active_unit_id = active_unit_id;
        this.turn_log = turn_log;
        this.battle_log = battle_log;
        this.rewards = rewards;
    }

    public static fromJson(data: any): BattleContextV2DTO {
        console.log(data);
        const playerUnits = data.player_units.map((unit) => {
            // More robust type checking - check for PC, pc, player, Player, etc.
            const isPlayerCharacter =
                unit.type &&
                (unit.type === "PC" ||
                    unit.type === "pc" ||
                    unit.type.toLowerCase() === "player" ||
                    unit.type.toLowerCase() === "playercharacter");

            if (isPlayerCharacter) {
                return PlayerCharacterDTO.fromCharacter(unit as PlayerCharacterDTO);
            } else {
                console.warn("Player unit has unexpected type:", unit.type, "for unit:", unit.name);
                // Still create as PlayerCharacterDTO since it's in player_units array
                return PlayerCharacterDTO.fromCharacter(unit as PlayerCharacterDTO);
            }
        });

        const enemyUnits = data.enemy_units.map((unit) => EnemyDTO.fromCharacter(unit as EnemyDTO));

        return new BattleContextV2DTO(
            data.battle_id,
            data.state,
            playerUnits,
            enemyUnits,
            data.turn_number,
            data.turn_order,
            data.active_unit_id,
            data.turn_log,
            data.battle_log,
            data.rewards
        );
    }

    public getAllUnits(): Array<CombatCharacterDTO> {
        return [...this.player_units, ...this.enemy_units];
    }

    public findUnitById(unitId: string): CombatCharacterDTO | undefined {
        return this.getAllUnits().find((unit) => unit.character_id === unitId);
    }

    public findLastAttack(): AttackDTO | null {
        if (!this.turn_log || this.turn_log.length === 0) {
            return null;
        }
        const lastTurn = this.turn_log[this.turn_log.length - 1];
        return lastTurn.action as AttackDTO;
    }
}

export class BattleRewardDTO {
    gold: number;
    xp: number;
}

export class NotificationDTO {
    message: string;
}

export class GameLocationDTO {
    scene_id: string;
    x: number;
    y: number;
}

// ============================================================
// New Game System DTOs
// ============================================================

export class StartingPartyMemberDTO {
    character_template_id: string; // References CharacterTemplate collection
    name_override?: string; // Override character name
    level_override?: number; // Override starting level
}

export class StartingInventoryItemDTO {
    item_definition_id: string; // References ItemDefinition
    quantity: number = 1;
}

export class NewGameDefaultsDTO {
    // Starting party - character templates to instantiate
    starting_party: StartingPartyMemberDTO[] = [];

    // Starting location - multiple resolution methods
    starting_scene_id?: string;
    starting_warp_point_id?: string; // UUID of WarpPoint
    fallback_location?: GameLocationDTO;

    // Starting resources
    starting_gold: number = 0;
    starting_items: StartingInventoryItemDTO[] = [];

    // Optional intro event
    intro_dialog_id?: string;
}

export class OngoingEventDTO {
    event_type: string; // "dialog", "cutscene", etc.
    event_id: string; // ID of the event
}

// ============================================================
// Game State DTOs
// ============================================================

export class GameStateDTO {
    player_account_id: string; // RENAMED from account_id
    save_id: string; // RENAMED from game_id
    project_id: string | null; // NEW
    publication_id: string | null; // NEW
    is_template: boolean; // NEW (always false for player saves)
    save_name: string; // NEW: User-friendly save name

    gold: number;
    game_location: GameLocationDTO;
    player_direction: string; // NEW: "up", "down", "left", "right" - tracks facing direction
    inventory: InventoryDTO;
    party: PartyDTO;

    // State tracking
    ongoing_events?: OngoingEventDTO[];
    player_variables?: Record<string, any>;
    switches?: Record<string, boolean>;

    // Display fields for UI (populated by backend)
    scene_name?: string; // Human-readable scene name (formatted from scene_id if not set)
    main_character_name?: string; // Main character's name
    main_character_level?: number; // Main character's level
    main_character_sprite_data?: SpriteDataDTO; // Main character's sprite data for map rendering
}

export class LoadGameDTO {
    game_state: GameStateDTO;
    party: PartyDTO;
}

// New Game Error Interface (matches backend error structure)
export interface NewGameError {
    code: string; // Error codes from backend (e.g., NO_STARTING_PARTY, MISSING_CHARACTER_TEMPLATE)
    message: string; // Human-readable error message
    details?: any; // Optional error details
}

export abstract class EventContentDTO {}

export class StatDefinitionDTO {
    stat_id: string;
    name: string;
}

export class StatSystemDTO {
    stats: Array<StatDefinitionDTO> = [];
}

// Placeholder for effect plugin (backend structure)
export class EffectPluginDTO {
    // To be defined based on backend implementation
}

// Game stat grids cache
export class GameStatGridsDTO {
    xp_grid?: XpGridDTO;
    // Other stat grids can be added here
}

export class XpGridDTO {
    grid_id: string;
    xp_values: { [level: number]: number };

    /**
     * Get required XP for a specific level
     * @param level The target level
     * @returns Required XP to reach that level, or fallback calculation if not in grid
     */
    get_required_xp(level: number): number {
        if (this.xp_values && this.xp_values[level] !== undefined) {
            return this.xp_values[level];
        }
        // Fallback formula: level * 100 (basic progression)
        return level * 100;
    }
}

// Character class system cache
export class ClassSystemDTO {
    classes: Array<CharacterClassDTO> = [];
}

export class GameConfigurationDTO {
    constructor(gameConfig?: GameConfigurationDTO) {
        if (gameConfig) {
            this.project_id = gameConfig.project_id;
            this.creator_account_id = gameConfig.creator_account_id;
            this.publication_id = gameConfig.publication_id;
            this.is_template = gameConfig.is_template;
            this.published = gameConfig.published;
            this.max_level = gameConfig.max_level;
            this.stat_system = gameConfig.stat_system;
            this.item_definitions = gameConfig.item_definitions;
            this.new_game_defaults = gameConfig.new_game_defaults;
            this.effect_plugin = gameConfig.effect_plugin;
            this.stat_grids = gameConfig.stat_grids;
            this.character_class_system = gameConfig.character_class_system;
        }
    }

    public static fromGameConfiguration(gameConfig: GameConfigurationDTO): GameConfigurationDTO {
        return new GameConfigurationDTO(gameConfig);
    }

    // Multi-tenant fields
    project_id: string; // RENAMED from game_id
    creator_account_id: string; // NEW
    publication_id: string | null; // NEW
    is_template: boolean; // NEW
    published: boolean; // NEW

    // Configuration
    max_level: number;
    stat_system: StatSystemDTO;
    effect_plugin: EffectPluginDTO;
    item_definitions: Array<ItemDefinitionDTO> = [];

    // Cache fields (synced from other collections)
    stat_grids?: GameStatGridsDTO;
    character_class_system?: ClassSystemDTO;

    // NEW GAME INITIALIZATION
    new_game_defaults: NewGameDefaultsDTO;

    public findStatDefinitionById(stat_id: string): StatDefinitionDTO {
        return this.stat_system.stats.find((stat_dto) => stat_dto.stat_id == stat_id);
    }
}

export abstract class GameEventDTO {
    event_id: string;
    trigger: string;
    type: string;
    content: EventContentDTO;
    next: Array<GameEventDTO>;
}

export class DialogContentDTO extends EventContentDTO {
    type = "dialog";
    message: string;
    wait_for_player_input: boolean;
}

export class ChoiceDialogContentDTO extends DialogContentDTO {
    message: string;
    choices: Array<ChoiceDTO>;
}

export class ActionContentDTO {
    action_id: string;
}

export class ActionEventDTO extends GameEventDTO {
    content: ActionContentDTO;
}

export class DialogDTO extends GameEventDTO {
    content: DialogContentDTO;
}

export class ChoiceDialogDTO extends DialogDTO {
    content: ChoiceDialogContentDTO;
}

export class ChoiceDTO {
    choice: string;
    next: Array<GameEventDTO>;
}

// deprecated
export class DialogEventDTO extends GameEventDTO {
    type = "DIALOG_EVENT";
    data: DialogDTO;
}

export class SpawnPointDTO {
    x: number;
    y: number;
}

/*export class TileSetDTO {
    tileset_name: string;
    tileset_key: string;    
}

export class TileLayerDTO {
    name: string;
    tilesets: TileSetDTO[];
    blocking_layer: boolean;    
}*/

/*export class MapDTO {
    id: string;
    tile_layers: TileLayerDTO[];
    spawn_point: SpawnPointDTO[];
    with_random_encounters: boolean;    
}*/

export class TilesetDTO {
    id: string;
    firstgid: number;
    columns: number;
    image: string;
    imageheight: number;
    imagewidth: number;
    margin: number;
    name: string;
    spacing: number;
    terrains: Array<any>;
    tilecount: number;
    tiledversion: string;
    tileheight: number;
    tiles: Array<any>;
    tilewidth: number;
    type: string;
    version: number;
}
export class EmbeddedTilesetDTO {
    columns: number;
    firstgid: number;
    image: string;
    imageheight: number;
    imagewidth: number;
    margin: number;
    name: string;
    spacing: number;
    terrains: Array<Object>;
    tilecount: number;
    tiledversion: string;
    tileheight: number;
    tiles: Array<Object>;
    tilewidth: number;
    type: string;
    version: number;
}

export class TiledPropertyDTO {
    name: string;
    type: string;
    value: string | boolean | number;
}

export class TiledObjectDTO {
    id: number;
    name: string;
    height: number;
    properties: Array<TiledPropertyDTO>;
    rotation: number;
    type: string;
    visible: boolean;
    width: number;
    x: number;
    y: number;
}

export class TiledLayerDTO {
    data: Array<number>;
    draworder: string;
    id: number;
    name: string;
    objects: Array<TiledObjectDTO>;
    properties: Array<TiledPropertyDTO>;
    opacity: number;
    type: string;
    visible: boolean;
    x: number;
    y: number;
    width: number;
    height: number;
}

export class TiledMapDTO {
    name: string;
    compressionlevel: number;
    editorsettings: Object;
    height: number;
    infinite: boolean;
    layers: Array<TiledLayerDTO>;
    nextlayerid: number;
    nextobjectid: number;
    orientation: string;
    properties: Array<TiledPropertyDTO>;
    renderorder: string;
    tiledversion: string;
    tileheight: number;
    tilesets: Array<EmbeddedTilesetDTO>;
    tilewidth: number;
    type: number;
    version: number;
    width: number;
}

export class AssetDTO {
    id: string | number;
    category: string;
    type: string;
    filename: string;
    content: Blob;
    path: string;
    sprite_width: number;
    sprite_height: number;
    online_image?: string;
}

export class EncountersDTO {
    common: string[];
    uncommon: string[];
    rare: string[];
    very_rare: string[];
}

export class WarpPointDTO {
    id: string;
    name: string;
    location: GameLocationDTO;
    is_default: boolean;
    target_scene_id: string | null;
    target_warp_id: string | null;
    conditions: string[];
    metadata: Record<string, any> | null;
}

export class SceneDTO {
    id: string;
    // Null when the backend cannot resolve the scene's map_id within its own project.
    tiled_map: TiledMapDTO | null;
    npcs: NPCDTO[];
    encounters: EncountersDTO;
    assets: AssetDTO[];
    warp_points: WarpPointDTO[];

    // Multi-tenant fields
    project_id: string | null;
    publication_id: string | null;
    save_id: string | null;
    is_template: boolean;
}

export class PartyDTO {
    main_character: string;
    characters: Array<PlayerCharacterDTO>;

    constructor(main_character: string, characters: Array<PlayerCharacterDTO>) {
        this.main_character = main_character;
        this.characters = characters;
    }

    public static fromParty(party: PartyDTO): PartyDTO {
        const characters = party.characters.map((unit) => PlayerCharacterDTO.fromCharacter(unit));
        return new PartyDTO(party.main_character, characters);
    }
}

/*export class ItemDTO {
    id: string;
    name: string;
    description: string;
    type: string;
    sprite_data: SpriteDataDTO;
    buy_price: number;
    sell_price: number;
}*/

/*export class OwnedItemDTO {
    uuid: number;
    item: ItemDTO;
    quantity: number;
    equipped_by: string;
    name: string;
    type: string;
}*/

export class ItemDefinitionDTO {
    item_definition_id: string;
    name: string;
    description: string;
    type: string;
    sprite_data: SpriteDataDTO | null;
    buy_price: number | null;
    sell_price: number | null;
}

export class InventoryItemDTO {
    uuid: number = 1;
    item_definition: ItemDefinitionDTO;
    quantity: number;
    equipped_by = null;
}

export class EquipmentDTO extends ItemDefinitionDTO {
    equipment_slot: string;
    equipped: Array<any>;
    used: Array<any>;
}
export class InventoryDTO {
    //    account_id: number;
    items: Array<InventoryItemDTO>;
}

export class ItemUsageRequestDTO {
    item_id: string;
    user_id: string;
    targets_ids: Array<string>;
}

export class ItemEquipRequestDTO {
    item_uuid: number;
    //    item_id: string;
    target_id: string;
}

export class SpriteConfigDTO {
    width: number;
    height: number;
}

export class AutomaticSkillTreeElementDTO {
    skill_id: string;
    skill_level: number;
    level_acquired: number;
}

export class SkillRequirementDTO {
    skill_id: string;
    skill_level: number;
}

export class SkillPrerequisiteDTO {
    known_skills: SkillRequirementDTO[];
}

export class UserDefinedSkillTreeElementDTO {
    skill_id: string;
    prerequisites: SkillPrerequisiteDTO[];
}

export class CharacterClassDTO {
    class_id: string;
    name: string;
    skill_tree_type: string;
    skill_tree: AutomaticSkillTreeElementDTO[];
}

export class ProjectDTO {
    id: string;
    name: string;
    description: string;
    creator_account_id: string;
    created_at: string;
    updated_at: string;
    published: boolean;
    publication_id: string | null;
    last_publish_date: string | null;
}

// ============================================================
// Dialog System DTOs (NEW - Event-Based Dialog System)
// ============================================================

// Speaker information
export interface SpeakerDTO {
    speaker_id: string;
    display_name: string;
    portrait_url?: string;
}

// Dialog content
export interface DialogContentData {
    message: string;
    speaker?: SpeakerDTO;
}

// Choice option
export interface DialogChoiceDTO {
    choice_id: string;
    text: string;
}

// Dialog message event (from backend)
export interface DialogMessageEvent {
    context_id: string;
    dialog_id: string;
    content: DialogContentData;
    portrait_online_image?: string | null; // Character portrait URL from backend (full path)
    choices: DialogChoiceDTO[];
    can_exit: boolean;
    metadata: {
        dialog_type: string;
        speaker_name?: string;
    };
}

// Dialog start event
// Updated: Now includes full dialog data (no need to wait for DIALOG_MESSAGE)
export interface DialogStartEvent {
    context_id: string;
    dialog_id: string;
    content: DialogContentData;
    portrait_online_image?: string | null; // Character portrait URL from backend (full path)
    choices: DialogChoiceDTO[];
    can_exit: boolean;
    metadata: {
        dialog_type: string;
        speaker_name?: string;
    };
}

// Dialog complete event
export interface DialogCompleteEvent {
    context_id: string;
    success?: boolean;
    abandoned?: boolean;
}

// Dialog error event
export interface DialogErrorEvent {
    context_id?: string;
    error_code: string;
    error_message: string;
}

// NPC interact request
// Note: save_id managed by backend session after load_game_request
export interface NPCInteractRequest {
    npc_id: string;
    scene_id: string;
}

// Dialog choice request (player response)
// Note: save_id managed by backend session after load_game_request
export interface DialogChoiceRequest {
    context_id: string;
    choice_id: string;
}

// Dialog continue request
// Note: save_id managed by backend session after load_game_request
export interface DialogContinueRequest {
    context_id: string;
}

// Dialog exit request
// Note: save_id managed by backend session after load_game_request
export interface DialogExitRequest {
    context_id: string;
}

// ============================================================
// Battle Event DTOs
// ============================================================

/**
 * Player input request - sent when battle system needs player to choose action
 */
export class PlayerInputRequestDTO {
    active_unit_id: string;
    active_unit: CombatCharacterDTO;

    constructor(data: any) {
        this.active_unit_id = data.active_unit_id;
        this.active_unit = new CombatCharacterDTO(data.active_unit);
    }

    public static fromJson(data: any): PlayerInputRequestDTO {
        return new PlayerInputRequestDTO(data);
    }
}

/**
 * Level-up data structure
 */
export interface LevelUpData {
    character_name: string;
    character_id: number;
    old_level: number;
    new_level: number;
    stat_increases: { [statId: string]: number };
}

/**
 * Battle end notification
 */
export class BattleEndDTO {
    state: string;
    rewards: any;
    level_ups?: LevelUpData[];

    constructor(state: string, rewards: any, level_ups?: LevelUpData[]) {
        this.state = state;
        this.rewards = rewards;
        this.level_ups = level_ups;
    }

    public static fromJson(data: any): BattleEndDTO {
        return new BattleEndDTO(data.state, data.rewards, data.level_ups);
    }
}
